import { asImageSrc } from "@prismicio/client"
import React from "react"

import { DEFAULT_LANGUAGE, useTranslation } from "@bounce/i18n"

import { StoreListSection } from "./components/StoreListSection"
import { useBreadcrumbs } from "./hooks/useBreadcrumbs"
import { Layout } from "@/components/LuggageStorage/Layout/Layout"
import type { CityData } from "@/libs/bounce/cities"
import type { NearbyStorageSpot } from "@/libs/bounce/stores/types"
import { useUrl } from "@/libs/router"
import { useInterpolations } from "@/screens/LuggageStorage/CityScreen/hooks/useInterpolations"
import { useSEO } from "@/screens/LuggageStorage/CityScreen/hooks/useSEO"

export type CityListViewScreenProps = {
  city: CityData
  stores: NearbyStorageSpot[]
}

const Separator = () => <div className="pt-section" />

export const CityListViewScreen = ({ city, stores }: CityListViewScreenProps) => {
  const interpolations = useInterpolations({ city })
  const { metaTitle, metaDescription } = useSEO({ city, interpolations, isPrismicMetaEnabled: true })
  const { absolutePath, getUrl } = useUrl()
  const { t } = useTranslation("marketing-ls-city")
  const breadcrumbs = useBreadcrumbs({ city })

  const alternates = [DEFAULT_LANGUAGE]
    .map(language => ({ language, href: getUrl({ absolute: true, language }) }))
    .concat([{ language: "x-default", href: getUrl({ absolute: true, language: DEFAULT_LANGUAGE }) }])

  return (
    <Layout
      alternates={alternates}
      breadcrumbs={breadcrumbs}
      headProps={{
        title: metaTitle,
        description: metaDescription,
        url: absolutePath,
        image: asImageSrc(city.image, { ar: "1.91:1", fit: "crop" }),
      }}
      languages={[DEFAULT_LANGUAGE]}
      from="City"
      searchQuery={city.query}
      hideNewsletterBanner
      hideDynamicAppBanner>
      <StoreListSection
        stores={stores}
        className="pt-8 lg:pt-20"
        from="City"
        path={absolutePath}
        searchQuery={city.query}
        title={t(
          "screen.cityListViewScreen.storesGrid.title",
          "{count, plural, =1 {Top Bounce luggage storage location} other {Top # Bounce luggage storage locations}} in {CITY_NAME}",
          {
            count: stores.length,
            CITY_NAME: interpolations.CITY_NAME,
          },
        )}
      />
      <Separator />
    </Layout>
  )
}
